// Portal Work — the catalog of properties a board card can display.
//
// Anthony's call (2026-07-26): rather than hardcoding a description preview, the
// card should work like Notion — pick which properties appear, rendered
// dynamically. The renderers already existed as discrete components in
// TaskBits; what was missing was a NAME for each one, a default, and somewhere
// to record the choice. That is all this file is.
//
// It is deliberately data, not JSX: TaskCard owns layout (which slot a property
// lands in), this owns identity (what a property IS, what it is called, whether
// it is on by default). Keeping them apart is what lets the list view adopt the
// same catalog later without inheriting the card's chip layout.
//
// NOT canon. These are UI affordances, not vocabulary — the area/priority/gate
// enums live in src/canon/taxonomy.json and are a different kind of thing. A
// property key here is a private contract between this file, TaskCard, and the
// userPrefs doc.

// `slot` is a hint to TaskCard about where a property belongs when shown:
//   head — the top row (identity + flags)
//   foot — the bottom row (people, dates, progress)
//   body — full-width under the title
// `grouped` names the board grouping that SUPPRESSES this property, because the
// column already says it. Grouping by area and printing "Website" on all twelve
// cards inside the Website column is noise, so selection loses to suppression.
export const CARD_PROPERTIES = [
	{ key: 'area', label: 'Area', slot: 'head', grouped: 'area', default: true },
	{ key: 'status', label: 'Status', slot: 'head', grouped: 'status', default: false },
	{ key: 'priority', label: 'Priority', slot: 'head', default: true },
	{ key: 'source', label: 'Source', slot: 'head', default: true },
	{ key: 'triage', label: 'Triage flags', slot: 'head', default: false },
	{ key: 'assignee', label: 'Assignee', slot: 'foot', grouped: 'assignee', default: true },
	{ key: 'due', label: 'Due date', slot: 'foot', default: true },
	{ key: 'subtasks', label: 'Sub-task progress', slot: 'foot', default: true },
	{ key: 'project', label: 'Project', slot: 'foot', default: false },
	{ key: 'effort', label: 'Effort', slot: 'foot', default: false },
	{ key: 'student', label: 'Linked student', slot: 'foot', default: false },
	{ key: 'comments', label: 'Comment count', slot: 'foot', default: false },
	{ key: 'description', label: 'Description preview', slot: 'body', default: false },
];

export const PROPERTY_KEYS = CARD_PROPERTIES.map((p) => p.key);

const BY_KEY = Object.fromEntries(CARD_PROPERTIES.map((p) => [p.key, p]));

// The catalog a given space offers (see spaceFields.js). A property whose field
// the space has turned off must not appear in the picker — but that alone is not
// enough, which is why normalizeProperties takes `fields` too: the selection is
// round-tripped through a user-writable document, so a key can arrive from a
// space that still had the field, or from a prefs namespace written before this
// gating existed. The picker and the read path have to agree, or a hidden
// property renders on cards that offer no way to turn it off.
//
// `fields` is a sparse map — it names only the fields a space actually gates, so
// anything it does not mention is offered. Absent (a direct mount, a test) means
// the full catalog, which is what every caller saw before this existed.
export function catalogFor(fields) {
	if (!fields) return CARD_PROPERTIES;
	return CARD_PROPERTIES.filter((p) => fields[p.key] !== false);
}

/** The default selection for a space — the catalog defaults, minus gated keys. */
export function defaultsFor(fields) {
	return catalogFor(fields)
		.filter((p) => p.default)
		.map((p) => p.key);
}

export function propertyMeta(key) {
	return BY_KEY[key] || null;
}

export const DEFAULT_PROPERTIES = CARD_PROPERTIES.filter((p) => p.default).map((p) => p.key);

// Normalize whatever came back from Firestore into a usable selection.
//
// Defensive on purpose: this value is round-tripped through a user-writable
// document, and it outlives the code that wrote it. A key retired in a later
// release would otherwise reach TaskCard and render nothing (or throw), and a
// null from a doc that has never been written must not read as "the user
// deselected everything" — those two are indistinguishable downstream unless
// they are separated HERE.
//   • null / undefined / not-an-array -> the defaults (never written yet)
//   • an array -> kept, minus any key this build no longer knows about
// An EMPTY array is a legitimate choice (a title-only card) and is preserved,
// which is exactly why the absent case has to be handled separately.
//
// `fields` (optional) additionally drops keys the current space has gated off —
// same reasoning as the retired-key case above, and it matters for the same
// reason: a stored selection outlives the surface that wrote it.
export function normalizeProperties(stored, fields) {
	const allowed = catalogFor(fields);
	if (!Array.isArray(stored)) return allowed.filter((p) => p.default).map((p) => p.key);
	const offered = new Set(allowed.map((p) => p.key));
	const seen = new Set();
	return stored.filter((k) => {
		if (!offered.has(k) || seen.has(k)) return false;
		seen.add(k);
		return true;
	});
}

// Is `key` shown, given the selection and how the board is currently grouped?
// Suppression beats selection — see `grouped` above.
export function isPropertyVisible(key, selected, groupBy) {
	const meta = BY_KEY[key];
	if (!meta) return false;
	if (meta.grouped && meta.grouped === groupBy) return false;
	return selected.includes(key);
}

// The selection in catalog order, so the card's chips keep a stable left-to-right
// order no matter what order the user toggled them in.
export function visibleProperties(selected, groupBy, slot) {
	return CARD_PROPERTIES.filter(
		(p) =>
			(!slot || p.slot === slot) && isPropertyVisible(p.key, selected, groupBy)
	);
}
